/**
 * Collection filter sidebar: multiple filter groups with checkboxes.
 * Within a group: OR (product matches if it has any checked tag).
 * Across groups: OR — all selected tags from all groups are pooled; product shows if it has any.
 * Toolbar tabs: OR across selected tabs.
 * Toolbar + sidebar: OR union — product shows if it matches the toolbar selection OR any sidebar tag.
 * Uses data-product-tags on .product-item and same HIDDEN_CLASS as toolbar filter.
 *
 * Debug logging is commented out in this file; search for "FILTER DEBUG" to restore.
 */
(function () {
  const SIDEBAR_SELECTOR = "[data-collection-filter-sidebar]";
  const GROUP_SELECTOR = "[data-filter-group]";
  const CHECKBOX_SELECTOR = "[data-filter-sidebar-tag]";
  const GRID_SELECTOR = "#main-collection-product-grid";
  const PRODUCT_SELECTOR = ".product-item";
  const HIDDEN_CLASS = "collection-toolbar-filter-hidden";
  const NO_RESULTS_SELECTOR = "[data-collection-no-results]";
  const TOOLBAR_SELECTOR = "[data-collection-toolbar-filters]";
  const TOOLBAR_FILTER_EVENT = "bioligent:collection-toolbar-filter";
  const TOOLBAR_ACTIVE_CLASS = "collection-toolbar__nav-link--active";
  const CLEAR_BAR_SELECTOR = "[data-collection-clear-filters-bar]";
  const CLEAR_FILTERS_SELECTOR = "[data-collection-clear-filters]";
  const TOOLBAR_RESET_SLOT_SELECTOR = "[data-collection-filter-toolbar-reset-slot]";

  /* FILTER DEBUG — uncomment the helpers + logFilters body below; remove the no-op logFilters() and filtersVerboseEnabled() stub that returns false.
  function filtersDebugEnabled() {
    try {
      if (typeof window === "undefined") return true;
      if (window.BIOLIGENT_DEBUG_COLLECTION_FILTERS === false) return false;
      if (/(?:^|[?&])debug_filters=0(?:&|$)/.test(String(window.location.search || "")))
        return false;
      return true;
    } catch (e) {
      return true;
    }
  }

  function filtersVerboseEnabled() {
    try {
      if (typeof window === "undefined") return false;
      if (window.BIOLIGENT_DEBUG_COLLECTION_FILTERS === "verbose") return true;
      return /(?:^|[?&])debug_filters=2(?:&|$)/.test(String(window.location.search || ""));
    } catch (e) {
      return false;
    }
  }

  function logFilters(message, data) {
    if (!filtersDebugEnabled()) return;
    if (data !== undefined) {
      console.log("[Bioligent filters]", message, data);
    } else {
      console.log("[Bioligent filters]", message);
    }
  }
  */

  function logFilters() {
    /* no-op while FILTER DEBUG block above is disabled */
  }

  function filtersVerboseEnabled() {
    return false;
  }

  /** Normalize tag for comparison: lowercase and collapse spaces to hyphen so "immune support" matches "immune-support". */
  function normalizeTag(tag) {
    if (!tag || typeof tag !== "string") return "";
    return tag.trim().toLowerCase().replace(/\s+/g, "-");
  }

  function getProductTags(productEl) {
    const raw = productEl.getAttribute("data-product-tags");
    if (!raw) return [];
    return raw.split(",").map(function (t) {
      return normalizeTag(t);
    });
  }

  function getToolbarSelectedTags() {
    const toolbar = document.querySelector(TOOLBAR_SELECTOR);
    if (!toolbar) return [];
    const tags = [];
    toolbar.querySelectorAll(".collection-toolbar__nav-link--active").forEach(function (btn) {
      tags.push(normalizeTag(btn.getAttribute("data-filter-tag") || ""));
    });
    return tags;
  }

  /** True when selected tabs impose a real tag constraint (not empty and no blank "show all" tab). */
  function toolbarConstrains(tags) {
    if (!tags || tags.length === 0) return false;
    for (var i = 0; i < tags.length; i++) {
      if (tags[i] === "") return false;
    }
    return true;
  }

  function hasActiveFilters(sidebar) {
    if (!sidebar) return false;
    const selectedGroups = getSelectedTagsByGroup(sidebar);
    const toolbarTags = getToolbarSelectedTags();
    return toolbarConstrains(toolbarTags) || selectedGroups.length > 0;
  }

  function updateClearFiltersBar(sidebar) {
    const active = hasActiveFilters(sidebar);
    document.querySelectorAll(CLEAR_BAR_SELECTOR).forEach(function (bar) {
      const btn = bar.querySelector(CLEAR_FILTERS_SELECTOR);
      if (!btn) return;
      bar.classList.toggle("is-active", active);
      btn.setAttribute("aria-hidden", active ? "false" : "true");
      btn.setAttribute("tabindex", active ? "0" : "-1");
    });
  }

  function clearAllFilters(sidebar) {
    if (!sidebar) return;
    sidebar.querySelectorAll(CHECKBOX_SELECTOR + ":checked").forEach(function (cb) {
      cb.checked = false;
    });
    const toolbar = document.querySelector(TOOLBAR_SELECTOR);
    if (toolbar) {
      toolbar.querySelectorAll("." + TOOLBAR_ACTIVE_CLASS).forEach(function (el) {
        el.classList.remove(TOOLBAR_ACTIVE_CLASS);
        el.setAttribute("aria-pressed", "false");
      });
    }
    applySidebarFilter();
  }

  function getSelectedTagsByGroup(sidebar) {
    const groups = sidebar.querySelectorAll(GROUP_SELECTOR);
    const result = [];
    groups.forEach(function (fieldset) {
      const checkboxes = fieldset.querySelectorAll(
        CHECKBOX_SELECTOR + ":checked",
      );
      if (checkboxes.length === 0) return;
      const tags = [];
      checkboxes.forEach(function (cb) {
        const tag = normalizeTag(cb.getAttribute("data-filter-sidebar-tag") || "");
        if (tag) tags.push(tag);
      });
      if (tags.length) result.push(tags);
    });
    return result;
  }

  function productMatchesGroups(productTags, selectedGroups) {
    if (selectedGroups.length === 0) return true;
    for (var i = 0; i < selectedGroups.length; i++) {
      const groupTags = selectedGroups[i];
      for (var j = 0; j < groupTags.length; j++) {
        if (productTags.indexOf(groupTags[j]) !== -1) return true;
      }
    }
    return false;
  }

  function productMatchesToolbarTags(productTags, toolbarTags) {
    if (!toolbarTags || toolbarTags.length === 0) return true;
    for (var i = 0; i < toolbarTags.length; i++) {
      if (toolbarTags[i] === "") return true;
      if (productTags.indexOf(toolbarTags[i]) !== -1) return true;
    }
    return false;
  }

  function updateNoResultsMessage(productItems) {
    const noResults = document.querySelector(NO_RESULTS_SELECTOR);
    if (!noResults) return;
    let visibleCount = 0;
    productItems.forEach(function (item) {
      if (!item.classList.contains(HIDDEN_CLASS)) visibleCount++;
    });
    noResults.style.display = visibleCount === 0 ? "" : "none";
  }

  /**
   * Parse ?filter=energy or ?filter=energy,bestsellers (comma-separated).
   * Matches toolbar data-filter-tag and sidebar data-filter-sidebar-tag after normalizeTag().
   */
  function getFilterTagsFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search || "");
      /* Prefer ?filter= — also accept ?tags= if ?filter= is missing (some CDNs/themes reserve "filter"). */
      const raw = params.get("filter") || params.get("tags");
      if (!raw || typeof raw !== "string") return [];
      return raw
        .split(",")
        .map(function (s) {
          return normalizeTag(s);
        })
        .filter(function (t) {
          return t.length > 0;
        });
    } catch (e) {
      return [];
    }
  }

  function applyToolbarButtonsForTags(tags) {
    if (!tags.length) return;
    const toolbar = document.querySelector(TOOLBAR_SELECTOR);
    if (!toolbar) return;
    toolbar.querySelectorAll("[data-filter-tag]").forEach(function (btn) {
      const tag = normalizeTag(btn.getAttribute("data-filter-tag") || "");
      if (tag && tags.indexOf(tag) !== -1) {
        btn.classList.add(TOOLBAR_ACTIVE_CLASS);
        btn.setAttribute("aria-pressed", "true");
      }
    });
  }

  function applySidebarCheckboxesForTags(sidebar, tags) {
    if (!sidebar || !tags.length) return;
    sidebar.querySelectorAll(CHECKBOX_SELECTOR).forEach(function (cb) {
      const tag = normalizeTag(cb.getAttribute("data-filter-sidebar-tag") || "");
      if (tag && tags.indexOf(tag) !== -1) {
        cb.checked = true;
      }
    });
  }

  /** When there is no toolbar and no sidebar checkboxes, hide products that do not match any URL tag (OR). */
  function applyDirectUrlFilterToGrid(tags) {
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid || !tags.length) return;
    const productItems = grid.querySelectorAll(PRODUCT_SELECTOR);
    productItems.forEach(function (item) {
      const productTags = getProductTags(item);
      let show = false;
      for (let i = 0; i < tags.length; i++) {
        if (tags[i] && productTags.indexOf(tags[i]) !== -1) {
          show = true;
          break;
        }
      }
      item.classList.toggle(HIDDEN_CLASS, !show);
      item.setAttribute("aria-hidden", show ? "false" : "true");
    });
    updateNoResultsMessage(productItems);
  }

  function applyInitialFiltersFromUrl(sidebar) {
    const tags = getFilterTagsFromUrl();
    if (!tags.length) return;
    applyToolbarButtonsForTags(tags);
    applySidebarCheckboxesForTags(sidebar, tags);

    const toolbarTags = getToolbarSelectedTags();
    const selectedGroups = getSelectedTagsByGroup(sidebar);
    const hasToolbar = toolbarConstrains(toolbarTags);
    const hasSidebar = selectedGroups.length > 0;

    if (hasToolbar || hasSidebar) {
      applySidebarFilter();
    } else {
      applyDirectUrlFilterToGrid(tags);
    }
  }

  function applySidebarFilter() {
    const sidebar = document.querySelector(SIDEBAR_SELECTOR);
    const grid = document.querySelector(GRID_SELECTOR);
    if (!sidebar || !grid) return;
    const productItems = grid.querySelectorAll(PRODUCT_SELECTOR);
    if (!productItems.length) {
      updateClearFiltersBar(sidebar);
      return;
    }

    const selectedGroups = getSelectedTagsByGroup(sidebar);
    const toolbarTags = getToolbarSelectedTags();
    const hasToolbar = toolbarConstrains(toolbarTags);
    const hasSidebar = selectedGroups.length > 0;

    logFilters("applySidebarFilter()", {
      toolbarTags,
      selectedGroups,
      hasToolbar,
      hasSidebar,
      toolbarPlusSidebarRule:
        hasToolbar && hasSidebar
          ? "OR — visible if matches toolbar OR any sidebar tag"
          : hasSidebar
            ? "sidebar only (OR across all groups)"
            : hasToolbar
              ? "toolbar only"
              : "none (show all)",
      productCount: productItems.length,
    });

    let visibleAfter = 0;
    const sampleLines = [];

    productItems.forEach(function (item, index) {
      const tags = getProductTags(item);
      const matchesToolbar = productMatchesToolbarTags(tags, toolbarTags);
      const matchesGroups = productMatchesGroups(tags, selectedGroups);
      let show;
      if (hasToolbar && hasSidebar) {
        show = matchesToolbar || matchesGroups;
      } else if (hasSidebar) {
        show = matchesGroups;
      } else if (hasToolbar) {
        show = matchesToolbar;
      } else {
        show = true;
      }
      item.classList.toggle(HIDDEN_CLASS, !show);
      item.setAttribute("aria-hidden", show ? "false" : "true");
      if (show) visibleAfter++;
      if (filtersVerboseEnabled() && index < 12) {
        const title = (item.getAttribute("data-product-title") || "").slice(0, 60);
        sampleLines.push({
          title,
          productTags: tags,
          matchesToolbar,
          matchesGroups,
          visible: show,
        });
      }
    });

    var resultPayload = { visibleCount: visibleAfter };
    if (filtersVerboseEnabled()) resultPayload.firstCards = sampleLines;
    logFilters("applySidebarFilter result", resultPayload);

    updateNoResultsMessage(productItems);
    updateClearFiltersBar(sidebar);
  }

  function init() {
    logFilters("collection-filter-sidebar.js ready");

    /* Register first so toolbar tabs work even if the grid is empty at init (e.g. theme editor). */
    document.addEventListener(TOOLBAR_FILTER_EVENT, function () {
      applySidebarFilter();
    });

    const sidebar = document.querySelector(SIDEBAR_SELECTOR);
    const grid = document.querySelector(GRID_SELECTOR);
    if (!sidebar || !grid) return;

    const checkboxes = sidebar.querySelectorAll(CHECKBOX_SELECTOR);

    if (checkboxes.length) {
      document.querySelectorAll(TOOLBAR_RESET_SLOT_SELECTOR).forEach(function (slot) {
        slot.removeAttribute("hidden");
      });

      document.querySelectorAll(CLEAR_FILTERS_SELECTOR).forEach(function (clearBtn) {
        clearBtn.addEventListener("click", function () {
          clearAllFilters(sidebar);
        });
      });
    }

    /* Apply ?filter= from URL before change listeners so programmatic checks do not double-fire. */
    applyInitialFiltersFromUrl(sidebar);

    const productItems = grid.querySelectorAll(PRODUCT_SELECTOR);
    if (!productItems.length) {
      if (checkboxes.length) {
        updateClearFiltersBar(sidebar);
      }
      return;
    }

    if (checkboxes.length) {
      checkboxes.forEach(function (cb) {
        cb.addEventListener("change", function () {
          applySidebarFilter();
        });
      });

      updateClearFiltersBar(sidebar);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
